///
/// EarthBrowser
///
/// Created by Matt Giger on 12/8/2024
/// Copyright (c) 2024 Geodata Labs
///

import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { ShareType } from "@shared-types"
import type { DatasetType } from "@/types/DatasetElement"
import type { LocalDataFile } from "./earthBrowserSlice"
import type { Placemark } from "./placemarksSlice"
import type { Story } from "./storiesSlice"

export type WindowType =
	"csv" |
	"shapefile" |
	"geojson" |
	"geopackage" |
	"geopackage-catalog" |
	"geopackage-detail" |
	"local-packages" |
	"earthquake" |
	"alert" |
	"volcano" |
	"buoy" |
	"webcam" |
	"wildfire" |
	"hurricane" |
	"sql-terminal" |
	"storage-manager" |
	"satellite" |
	"ogc-collection" |
	"nexrad" |
	"radar" |
	"location" |
	"saved-views" |
	"image"

export type WindowStatus = "loading" | "ready" | "error"

/** Tabs of the Views menu (header/ViewsMenu.tsx) */
export type LibraryTab = "views" | "places" | "stories" | "recent"

/** One geolocation fix from the device (services/userLocationService.ts) */
export interface UserLocationFix {
	lat: number
	lon: number
	/** 1-sigma horizontal accuracy radius (m) */
	accuracyM: number
	altitudeM: number | null
	speedMps: number | null
	/** Course over ground (degrees clockwise from true north), null while stationary */
	courseDeg: number | null
	timestamp: number
}

/**
 * Device location tracking: the blue "you are here" marker and the
 * follow / heading camera modes read from here. Ephemeral: the watch
 * restarts from "off" on every launch.
 * - off: not watching, no marker
 * - locating: permission prompt / first fix pending
 * - tracking: watch running, marker shown (camera may or may not follow:
 *   that is dialog.cameraMode "follow" | "heading")
 * - denied / unavailable: the last attempt failed; a tap retries
 */
export interface UserLocationState {
	status: "off" | "locating" | "tracking" | "denied" | "unavailable"
	fix: UserLocationFix | null
	/** Device compass heading (degrees clockwise from north), null when unknown */
	headingDeg: number | null
	/** A compass is plausibly available (touch device with DeviceOrientationEvent) */
	headingSupported: boolean
}

// DockMode determines how a window can be displayed
// - 'window-only': Can only be shown as a floating window
// - 'dock-only': Can only be shown docked in the bottom drawer
// - 'both': Can be shown as either a window or docked (default)
export type DockMode = "window-only" | "dock-only" | "both"

// Tab in the bottom drawer
export interface DrawerTab {
	windowId: string
	order: number
}

// Bottom drawer state
export interface BottomDrawerState {
	isOpen: boolean
	height: number
	tabs: DrawerTab[]
	activeTabId: string | null
}

/**
 * Cloud identity of an imported dataset window: set after a successful
 * "Save to cloud" upload, or when the window was opened from a cloud
 * source (dataset share, story dataset mount). Story capture reads this
 * to reference the dataset instead of embedding it.
 */
export interface CloudDatasetLink {
	/** user_dataset row uuid */
	id: string
	name: string
	filename: string
	format: "geojson" | "csv" | "image"
	cdnUrl: string
	featureCount?: number
}

/** Geographic rectangle in degrees (image ground overlays) */
export interface GeoBounds {
	west: number
	south: number
	east: number
	north: number
}

/**
 * Ground overlay placement for "image" windows: where the picture drapes
 * on the globe and how transparent it is. The GroundOverlayActivity in
 * the engine watches these to drive the earth-tile shader.
 */
export interface ImageOverlayState {
	bounds: GeoBounds
	/** 0..1 */
	opacity: number
	naturalWidth: number
	naturalHeight: number
}

// Window state interface for dataset browser windows
// Note: Icons and colors are NOT stored here to keep Redux state serializable
export interface DatasetBrowserWindowState {
	id: string
	x: number
	y: number
	width: number
	height: number
	isMaximized: boolean
	isVisible: boolean
	zIndex: number
	title: string
	// Data source reference (for connecting window to data)
	dataSourceId?: string
	windowType?: WindowType
	// GeoPackage-specific metadata (for layer visibility control)
	catalogId?: string
	activeLayerName?: string
	// Matplotlib plot-specific data
	plotImageData?: string // base64-encoded image data for matplotlib plots
	// UI options
	status?: string // Optional status text shown below title in minimized view
	// Dock mode - determines if window can be docked, windowed, or both (default: 'both')
	dockMode?: DockMode
	// Whether this window is currently docked in the bottom drawer
	isDockedInDrawer?: boolean
	// Cloud identity (set after upload or when opened from a cloud source)
	cloudDataset?: CloudDatasetLink
	// Ground overlay placement (only on windowType "image")
	imageOverlay?: ImageOverlayState
}

/**
 * Earthquake cutout (cross-section block) open for a quake. The React info
 * panel toggles it; the earthquakes layer builds the block and fills in the
 * sequence summary once the local SQLite cluster query returns
 */
/**
 * Payload of setWebcamSheet. Opened by long-press / right-click on a
 * webcam cluster (WebcamCloudNode) or from the focused camera's panel.
 */
export interface WebcamSheetState {
	/** Camera ids, best first (cluster rank or distance order) */
	ids: string[]
	/** Headline, e.g. "822 webcams" */
	title: string
	/** Where the cameras are, for the sheet's "Zoom to area" action */
	lat: number
	lon: number
	/** Radius of the area in km */
	extentKm: number
	/** Id of the camera the sheet was opened from, highlighted in the grid */
	currentId?: string
}

export interface QuakeCutoutState {
	quakeId: string
	status: "loading" | "ready" | "error"
	/** Headline facts, set the instant the cutout is requested so the HUD can show them */
	quake?: QuakeCutoutHeadline
	/**
	 * "mainshock": the selected quake is the largest in its cluster and big
	 * enough for sequence statistics to mean anything. "member": it is part
	 * of ongoing activity (a swarm or background seismicity) and only the
	 * block, history and regional baseline are shown
	 */
	role?: "mainshock" | "member"
	/** Largest event of the cluster when the selected quake isn't it */
	clusterLargest?: { id: string; mag: number }
	/** Which asynchronous parts have landed (HUD rows show skeletons until then) */
	stages: { sequence: boolean; terrain: boolean; history: boolean; detail: boolean; slab: boolean; exposure: boolean }
	/** HUD hidden behind its chip; the block and the quake's focus stay */
	hudCollapsed: boolean
	summary?: QuakeCutoutSummary
	/** Aftershock magnitude vs. time points for the HUD chart */
	chart?: QuakeCutoutChart
}

/** Payload of updateQuakeCutout: a shallow merge into the open cutout's state */
export type QuakeCutoutUpdate = { quakeId: string }
	& Partial<Omit<QuakeCutoutState, "quakeId" | "stages" | "hudCollapsed">>
	& { stages?: Partial<QuakeCutoutState["stages"]> }

export interface QuakeCutoutHeadline {
	mag: number
	place: string
	eventTime: string
	depthKm: number
	lat: number
	lon: number
	alert: string | null
	tsunami: boolean
	/** USGS ShakeMap maximum intensity, when published */
	mmi: number | null
}

export interface QuakeCutoutChart {
	/** Mainshock time (epoch ms); the chart's time origin */
	mainshockTimeMs: number
	mainshockMag: number
	/** [hours after the mainshock, magnitude, depth km] per aftershock */
	points: Array<[number, number, number]>
	/** Depth the color ramp spans (the box floor), km */
	depthFloorKm: number
	maxHours: number
	magMin: number
	magMax: number
}

export interface QuakeCutoutSummary {
	/** Events in the mainshock's cluster, including the mainshock */
	sequenceCount: number
	foreshockCount: number
	largestAftershockMag?: number
	/** Strike of the block's long axis, degrees clockwise from north */
	strikeDeg: number
	lengthKm: number
	depthFloorKm: number
	depthScale: number
	terrainExaggeration?: number
	/** Set when bathymetry covers the footprint and is stretched less than the land */
	seaFloorExaggeration?: number
	/** Historical catalog facts around the epicenter (once that dataset is synced) */
	history?: QuakeCutoutHistory
	/** Sequence statistics, fault-plane geometry and the USGS forecast */
	analysis?: QuakeCutoutAnalysis
	/** USGS Slab2 subduction interface under the block (undefined = not a subduction zone) */
	slab?: QuakeCutoutSlab
	/** Estimated shaking at nearby cities (mainshocks M ≥ 5) */
	exposure?: QuakeCutoutExposure
	/** Shallow-water tsunami travel time baked into the lid (offshore mainshocks M ≥ 6.5) */
	tsunami?: { edgeArrivalMin: number; isochronMin: number }
}

export interface QuakeCutoutSlab {
	zone: string
	depthAtEpicenterKm?: number
	strikeDeg?: number
	dipDeg?: number
	hypocenterOffsetKm?: number
	relation?: "interface" | "overriding" | "slab"
	minDepthKm: number
	maxDepthKm: number
	/** False when the interface lies entirely below the box floor */
	drawn: boolean
}

export interface QuakeCutoutExposure {
	model: string
	radiusKm: number
	populationAtOrAbove: Record<number, number>
	cities: Array<{ name: string; country: string; population: number; mmi: number; distanceKm: number }>
	cityCount: number
}

export interface QuakeCutoutAnalysis {
	bValue?: { b: number; stdErr: number; mc: number; n: number }
	bathExpected: number
	largestAftershock?: number
	omori?: { firstDay: number; lastDay: number; spanDays: number }
	planes?: {
		fit?: { strikeDeg: number; dipDeg: number; dipResolved: boolean; trendResolved: boolean; n: number; flatness: number }
		nodalPlanes?: { np1: NodalPlaneState; np2: NodalPlaneState; source: string }
		matchedPlane?: 1 | 2
		angleToNp1?: number
		angleToNp2?: number
	}
	forecast?: {
		modelName: string
		p?: number
		c?: number
		b?: number
		mc?: number
		creationTimeMs: number
		expireTimeMs: number
		reviewed: boolean
		windows: Array<{
			label: string
			timeStartMs: number
			timeEndMs: number
			bins: Array<{ magnitude: number; probability: number; median?: number }>
		}>
	}
}

export interface NodalPlaneState {
	strikeDeg: number
	dipDeg: number
	rakeDeg: number
}

export interface QuakeCutoutHistory {
	minMag: number
	sinceYear: number
	countNearby: number
	radiusKm: number
	largest?: { mag: number; year: number; distanceKm: number; depthKm: number }
	lastM6Year?: number
	lastM7Year?: number
	hiddenDeeper: number
	regionalB?: { b: number; stdErr: number; n: number }
	/** Historical events drawn as ghosts inside the block */
	ghostCount: number
}

/**
 * Storm column (tropical cyclone cutout) open for a focused storm. The
 * React info panel toggles it; the hurricanes layer builds the block and
 * fills in the IR structure, wind fit and forecast summaries as they land
 */
export interface StormCutoutState {
	stormId: string
	status: "loading" | "ready" | "error"
	/** Headline facts, set the instant the column is requested so the HUD can show them */
	storm?: StormCutoutHeadline
	/** Which asynchronous parts have landed (HUD rows show skeletons until then) */
	stages: { frames: boolean; wind: boolean; forecast: boolean; sst: boolean; buoys: boolean; lightning: boolean }
	/** HUD hidden behind its chip; the block and the storm's focus stay */
	hudCollapsed: boolean
	summary?: StormCutoutSummary
}

/** Payload of updateStormCutout: a shallow merge into the open column's state */
export type StormCutoutUpdate = { stormId: string }
	& Partial<Omit<StormCutoutState, "stormId" | "stages" | "hudCollapsed">>
	& { stages?: Partial<StormCutoutState["stages"]> }

export interface StormCutoutHeadline {
	name: string
	classification: string
	basin: string
	category: number
	maxWindKt: number | null
	gustKt: number | null
	mslpMb: number | null
	advisoryNum: string | null
	advisoryDate: string
	lat: number
	lon: number
	movementDir: number | null
	movementSpeedKt: number | null
	/** Sustained wind change over the last 24 h of the observed track, kt */
	change24hKt: number | null
}

export interface StormCutoutSummary {
	/** Block extent along the motion axis, km */
	lengthKm: number
	widthKm: number
	/** Printed vertical stretch of the height axis */
	heightScale: number
	/** Height grid spacing, km */
	tickKm: number
	/** Block orientation: motion direction, degrees clockwise from north */
	motionDeg: number
	/** Storm-centered IR frames found for the lid */
	frames?: StormCutoutFrames
	/** Statistics of the displayed IR frame */
	structure?: StormCutoutStructure
	/** Parametric surface wind model fitted to the advisory */
	wind?: StormCutoutWind
	/** Intensity history + forecast for the HUD chart, plus the ensemble spread */
	forecast?: StormCutoutForecast
	/** Sea surface temperature facts (undefined until the SST frame is cut, or when none is synced) */
	environment?: StormCutoutEnvironment
	/** Buoys inside the footprint with their latest observations */
	buoys?: StormCutoutBuoy[]
	/** SHIPS environmental diagnostics at the analysis hour (NHC basins only) */
	ships?: StormCutoutShips
	/** GOES GLM lightning inside the block (undefined until the shard is fetched) */
	lightning?: StormCutoutLightning
}

export interface StormCutoutLightning {
	/** A GOES lightning mapper sees this storm at all (Himawari has none) */
	covered: boolean
	/** Flashes for the storm over the published window (48 h) */
	total: number
	spanHours: number
	/** Flashes in the hour ending at referenceT, and the hour before it */
	lastHour: number
	priorHour: number
	/** Of lastHour, those within innerCoreKm of the center at flash time */
	innerCoreLastHour: number
	innerCoreKm: number
	/** Hourly bins ending at referenceT, oldest first */
	hourly: Array<{ t: number; count: number; innerCore: number }>
	peak?: { t: number; count: number }
	/** End of the "last hour": the scene clock clamped into the data */
	referenceT: number
}

export interface StormCutoutShips {
	/** Model run, ISO */
	run: string
	/** 850–200 hPa shear now and at +24 h, kt, with the heading the vector points toward */
	shearKt: number | null
	shearDirDeg: number | null
	shear24Kt: number | null
	sstC: number | null
	potentialIntensityKt: number | null
	rhMidPct: number | null
	oceanHeatContent: number | null
	landKm: number | null
	/** Rapid-intensification probabilities, consensus and SHIPS-RII, 0–100 */
	ri: Array<{ thresholdKt: number; hours: number; consensusPct: number; riiPct: number; climatologyPct: number | null }>
}

export interface StormCutoutEnvironment {
	/** Analysis time of the SST frame, ISO */
	frameIso: string
	sstUnderCenterC?: number
	/** First forecast time over water cooler than 26.5 °C or over land; undefined = warm water all the way */
	warmWaterEndsT?: number
	alreadyCool: boolean
	/** Mean SST change along the last day of track between the two frames, °C */
	coldWakeC?: number
	/** Hours between the two frames the wake was measured from */
	wakeSpanHours?: number
}

export interface StormCutoutBuoy {
	stationId: string
	name: string
	lat: number
	lon: number
	/** Observation time, ISO; null when the station has no recent report */
	time: string | null
	pressureHpa: number | null
	windGustMps: number | null
	windSpeedMps: number | null
	waveHeightM: number | null
	waterTempC: number | null
	/** Distance from the storm center, km */
	distanceKm: number
}

export interface StormCutoutFrames {
	count: number
	oldestIso: string
	newestIso: string
	satellite: string
	/** Frame currently on the lid */
	displayedIso: string
}

export interface StormCutoutStructure {
	/** Coldest cloud top in the frame, °C, and its lapse-rate height, km */
	coldestTopC: number
	coldestTopKm: number
	/** Fraction of the frame colder than -70 °C (deep convection) */
	coldFraction: number
	/** The same fraction over the loaded frames, oldest first, for the trend sparkline */
	coldFractionTrend: Array<{ t: number; fraction: number }>
	/** Eye diameter from the warm core inside the eyewall, km; undefined when no warm core is found */
	eyeDiameterKm?: number
	/** Warm-core temperature contrast against the eyewall ring, K */
	eyeContrastK?: number
}

export interface StormCutoutWind {
	source: "fit" | "climatology"
	rmwKm: number
	/** Where the RMW came from: the NHC best track, or Knaff et al. climatology */
	rmwSource: "atcf" | "climatology"
	/** Holland B per quadrant (NE, SE, SW, NW) */
	b: [number, number, number, number]
	/** ATCF best-track structure at the newest fix (NHC basins only) */
	bestTrack?: StormCutoutBestTrack
	/** Mean 34-kt extent, km, when the advisory carries radii */
	r34MeanKm?: number
	/** Ratio of the largest to the smallest 34-kt quadrant radius */
	asymmetry?: number
	/** Integrated kinetic energy over the ≥ 34 kt field, TJ */
	ikeTJ?: number
}

export interface StormCutoutBestTrack {
	/** Newest fix time, ISO */
	fixIso: string
	rmwKm: number | null
	/** RMW about 12 h and 24 h before the newest fix, km, for the contraction trend */
	rmw12hKm: number | null
	rmw24hKm: number | null
	eyeKm: number | null
	/** Outer closed isobar: pressure and radius */
	pociMb: number | null
	rociKm: number | null
	gustKt: number | null
	depth: "S" | "M" | "D" | "X" | null
	/** Deck's RMW series (oldest first) for the HUD sparkline */
	rmwSeries: Array<[number, number]>
}

export interface StormCutoutForecast {
	/** [epoch ms, kt] */
	observed: Array<[number, number]>
	forecast: Array<[number, number]>
	/** Observed points with both wind and pressure: [epoch ms, kt, hPa], for the wind–pressure chart */
	windPressure: Array<[number, number, number]>
	/** SHIPS statistical intensity forecast (with land), [epoch ms, kt] */
	ships?: Array<[number, number]>
	/** Ensemble 10th / 90th percentile of member wind per forecast hour, [epoch ms, kt] */
	ensP10?: Array<[number, number]>
	ensP90?: Array<[number, number]>
	/** Share of ensemble members at or above each category per tau */
	categoryShare?: Array<{ t: number; category: number; share: number; members: number }>
	ensembleRun?: string
	peak?: { kt: number; t: number }
	landfallT?: number
}

export interface DialogState {
	betaTestOpen: boolean
	authOpen: boolean
	// Founding-supporter offer redemption card, opened only by the
	// ?offer=supporter deep link from the offer email (never from UI)
	supporterOfferOpen: boolean
	profileOpen: boolean
	subscribeOpen: boolean
	checkoutOpen: boolean
	packageLoadingOpen: boolean
	welcomeOpen: boolean
	onboardingOpen: boolean
	/** Long-press / right-click drop-pin context menu (canvas CSS coords + hit lat/lon degrees) */
	dropPinMenu: { x: number; y: number; lat: number; lon: number } | null
	/** Webcam contact sheet: the cameras of a cluster (or near a camera) as a browsable grid */
	webcamSheet: WebcamSheetState | null
	layersPanelOpen: boolean
	// Phones: the layer legend open as a bottom sheet (legends/LegendSheet.tsx)
	legendSheet: DatasetType | null
	// Legend tray collapsed to its toggle button. Session-only on purpose:
	// a reload, or enabling a layer that has a legend, brings it back
	legendTrayHidden: boolean
	// ⌘K command palette (controls/search/CommandPalette.tsx): open state
	// plus the query to seed it with (the Layers panel hands its filter
	// text over when the user escalates to a global search)
	commandPalette: { open: boolean; initialQuery: string }
	selectedDevtoolsTable: string
	selectedSideTable: string | null
	// Data browser dialogs (ephemeral, not persisted)
	shapefileBrowserOpen: boolean
	geojsonBrowserOpen: boolean
	// Open dataset files (ephemeral, not persisted)
	openCSVFiles: string[] // Array of CSV filenames currently open
	// Data browser window states (ephemeral, not persisted)
	csvBrowserWindowState?: { x: number; y: number; width: number; height: number; isMaximized?: boolean }
	shapefileBrowserWindowState?: { x: number; y: number; width: number; height: number; isMaximized?: boolean }
	geojsonBrowserWindowState?: { x: number; y: number; width: number; height: number; isMaximized?: boolean }
	geopackageBrowserWindowState?: { x: number; y: number; width: number; height: number; isMaximized?: boolean }
	// Camera state (ephemeral)
	rapidCameraMovement: boolean
	// Max clock animation speed imposed by the focused entity (ephemeral)
	// null = no cap. Set by e.g. satellite focus so orbits stay watchable.
	clockSpeedCap: number | null
	// Mobile: an anchored info panel is showing as a bottom sheet, so the
	// time control (which shares the bottom edge) hides itself (ephemeral)
	mobileInfoSheetOpen: boolean
	// Mobile: canvas height (px) that info sheet covers, so the engine can
	// keep the focused entity in the part of the globe left visible (ephemeral)
	mobileInfoSheetHeight: number
	// Earthquake cutout block open for a focused quake (null = closed)
	quakeCutout: QuakeCutoutState | null
	// Storm column block open for a focused tropical cyclone (null = closed)
	stormCutout: StormCutoutState | null
	// Wind threshold the NHC wind speed probability swath / contours show (kt)
	stormWindProbabilityKt: 34 | 50 | 64
	// Mobile: canvas height (px) the cutout HUD bottom sheet covers, so the
	// engine can center the block in the part of the globe left visible
	// (0 on desktop, where the HUD is a side column)
	quakeCutoutSheetHeight: number
	// Multi-window management (ephemeral, not persisted)
	datasetWindows: Record<string, DatasetBrowserWindowState>
	windowOrder: string[] // Window IDs in z-order (back to front)
	nextZIndex: number
	// Bottom drawer state (ephemeral, not persisted)
	bottomDrawer: BottomDrawerState
	// Local data scan results (ephemeral, re-scanned on launch)
	localDataFiles: LocalDataFile[]
	localDataLastScan: number | null
	localDataScanErrors: string[]
	// Git integration (ephemeral, not persisted)
	gitHistoryOpen: boolean
	gitCurrentBranch: string | null
	gitHasChanges: boolean
	// Saved-view save toast: id of the just-saved view, for inline rename
	savedViewToastId: string | null
	// Automated camera motion mode (ephemeral, set by CameraDirector).
	// follow / heading: the camera tracks the device location (heading
	// also turns the view to the compass heading)
	cameraMode: "free" | "orbit" | "tour" | "tourPaused" | "follow" | "heading"
	// Device location tracking (ephemeral, set by userLocationService)
	userLocation: UserLocationState
	// Story tour being played (ephemeral, set by StoryPlayer): drives the
	// story card overlay. Survives a pause (cameraMode tourPaused) so the
	// card can offer "Resume story".
	activeStory: { storyId: string; stopIndex: number; guided: boolean } | null
	// Story open in the editor panel (null = closed)
	storyEditorId: string | null
	// Click-to-draw annotation authoring: while set, globe clicks append
	// points (marker = 1 click, arc = 2+, region = 3+ ring). The editor
	// commits the finished geometry as a StoryAnnotation.
	storyDrawing: {
		storyId: string
		kind: "arc" | "marker" | "region"
		points: { lat: number; lon: number }[]
	} | null
	// Views menu (top-right library popover): last active tab for the
	// session, plus the sort mode of the Saved Views and My Places lists
	libraryTab: LibraryTab
	savedViewsSortMode: "recent" | "alpha" | "nearby"
	myPlacesSortMode: "recent" | "alpha" | "nearby"
	// Device seat-limit eviction token from a blocked sign-in (opens the
	// DeviceLimitDialog; null = closed). Ephemeral, never persisted.
	deviceLimitToken: string | null
	// Share link the app was launched with (?s=<slug>): drives the branded
	// recipient banner. Ephemeral — never persisted.
	activeShare: { slug: string; shareType: ShareType; title: string } | null
	// Placemark snapshot from a placemark share, rendered by
	// PlacemarksLayer as a preview pin without entering the user's
	// placemarks (the banner's "Add to My Places" copies it over)
	sharedPlacemarkPreview: Placemark | null
	// Story snapshot from a story share, playable by StoryPlayer without
	// entering the user's stories (the banner's "Save story" copies it over)
	sharedStoryPreview: Story | null
}

const initialState: DialogState = {
	betaTestOpen: false,
	authOpen: false,
	supporterOfferOpen: false,
	profileOpen: false,
	subscribeOpen: false,
	checkoutOpen: false,
	packageLoadingOpen: false,
	welcomeOpen: false,
	onboardingOpen: false,
	dropPinMenu: null,
	webcamSheet: null,
	layersPanelOpen: false,
	legendSheet: null,
	legendTrayHidden: false,
	commandPalette: { open: false, initialQuery: "" },
	selectedDevtoolsTable: "",
	selectedSideTable: null,
	shapefileBrowserOpen: false,
	geojsonBrowserOpen: false,
	openCSVFiles: [],
	csvBrowserWindowState: undefined,
	shapefileBrowserWindowState: undefined,
	geojsonBrowserWindowState: undefined,
	geopackageBrowserWindowState: undefined,
	rapidCameraMovement: false,
	clockSpeedCap: null,
	mobileInfoSheetOpen: false,
	mobileInfoSheetHeight: 0,
	quakeCutout: null,
	stormCutout: null,
	stormWindProbabilityKt: 34,
	quakeCutoutSheetHeight: 0,
	datasetWindows: {},
	windowOrder: [],
	nextZIndex: 1000,
	bottomDrawer: {
		isOpen: false,
		height: 400,
		tabs: [],
		activeTabId: null,
	},
	localDataFiles: [],
	localDataLastScan: null,
	localDataScanErrors: [],
	gitHistoryOpen: false,
	gitCurrentBranch: null,
	gitHasChanges: false,
	savedViewToastId: null,
	cameraMode: "free",
	userLocation: { status: "off", fix: null, headingDeg: null, headingSupported: false },
	activeStory: null,
	storyEditorId: null,
	storyDrawing: null,
	libraryTab: "views",
	savedViewsSortMode: "recent",
	myPlacesSortMode: "recent",
	deviceLimitToken: null,
	activeShare: null,
	sharedPlacemarkPreview: null,
	sharedStoryPreview: null,
}

export const dialogSlice = createSlice({
	name: "dialog",
	initialState,
	reducers: {
		setBetaTestOpen: (state, action: PayloadAction<boolean>) => {
			state.betaTestOpen = action.payload
		},
		setProfileOpen: (state, action: PayloadAction<boolean>) => {
			state.profileOpen = action.payload
		},
		setAuthOpen: (state, action: PayloadAction<boolean>) => {
			state.authOpen = action.payload
		},
		setActiveShare: (state, action: PayloadAction<{ slug: string; shareType: ShareType; title: string } | null>) => {
			state.activeShare = action.payload
		},
		setSharedPlacemarkPreview: (state, action: PayloadAction<Placemark | null>) => {
			state.sharedPlacemarkPreview = action.payload
		},
		setSharedStoryPreview: (state, action: PayloadAction<Story | null>) => {
			state.sharedStoryPreview = action.payload
		},
		setSupporterOfferOpen: (state, action: PayloadAction<boolean>) => {
			state.supporterOfferOpen = action.payload
		},
		setSubscribeOpen: (state, action: PayloadAction<boolean>) => {
			state.subscribeOpen = action.payload
		},
		setCheckoutOpen: (state, action: PayloadAction<boolean>) => {
			state.checkoutOpen = action.payload
		},
		setSelectedSideTable: (state, action: PayloadAction<string | null>) => {
			state.selectedSideTable = action.payload
		},
		setPackageLoadingOpen: (state, action: PayloadAction<boolean>) => {
			state.packageLoadingOpen = action.payload
		},
		setWelcomeOpen: (state, action: PayloadAction<boolean>) => {
			state.welcomeOpen = action.payload
		},
		setOnboardingOpen: (state, action: PayloadAction<boolean>) => {
			state.onboardingOpen = action.payload
		},
		setDropPinMenu: (state, action: PayloadAction<{ x: number; y: number; lat: number; lon: number } | null>) => {
			state.dropPinMenu = action.payload
		},
		setWebcamSheet: (state, action: PayloadAction<WebcamSheetState | null>) => {
			state.webcamSheet = action.payload
		},
		setLayersPanelOpen: (state, action: PayloadAction<boolean>) => {
			state.layersPanelOpen = action.payload
		},
		setLegendSheet: (state, action: PayloadAction<DatasetType | null>) => {
			state.legendSheet = action.payload
		},
		setLegendTrayHidden: (state, action: PayloadAction<boolean>) => {
			state.legendTrayHidden = action.payload
		},
		// Open the ⌘K palette, optionally seeded with a query
		openCommandPalette: (state, action: PayloadAction<string | undefined>) => {
			state.commandPalette = { open: true, initialQuery: action.payload ?? "" }
		},
		closeCommandPalette: (state) => {
			state.commandPalette = { open: false, initialQuery: "" }
		},
		setSelectedDevtoolsTable: (state, action: PayloadAction<string>) => {
			state.selectedDevtoolsTable = action.payload
		},

		// Data browser dialogs (CSV, Shapefile, GeoJSON)
		setShapefileBrowserOpen: (state, action: PayloadAction<boolean>) => {
			state.shapefileBrowserOpen = action.payload
		},
		setGeoJSONBrowserOpen: (state, action: PayloadAction<boolean>) => {
			state.geojsonBrowserOpen = action.payload
		},

		// Open dataset files management
		addOpenCSVFile: (state, action: PayloadAction<string>) => {
			const filename = action.payload
			if (!state.openCSVFiles.includes(filename)) {
				state.openCSVFiles.push(filename)
			}
		},
		removeOpenCSVFile: (state, action: PayloadAction<string>) => {
			const filename = action.payload
			state.openCSVFiles = state.openCSVFiles.filter(f => f !== filename)
		},
		clearOpenCSVFiles: (state) => {
			state.openCSVFiles = []
		},

		// Data browser window states (for movable/resizable browsers)
		setCSVBrowserWindowState: (state, action: PayloadAction<{ x: number; y: number; width: number; height: number; isMaximized?: boolean }>) => {
			state.csvBrowserWindowState = action.payload
		},
		setShapefileBrowserWindowState: (state, action: PayloadAction<{ x: number; y: number; width: number; height: number; isMaximized?: boolean }>) => {
			state.shapefileBrowserWindowState = action.payload
		},
		setGeoJSONBrowserWindowState: (state, action: PayloadAction<{ x: number; y: number; width: number; height: number; isMaximized?: boolean }>) => {
			state.geojsonBrowserWindowState = action.payload
		},
		setGeoPackageBrowserWindowState: (state, action: PayloadAction<{ x: number; y: number; width: number; height: number; isMaximized?: boolean }>) => {
			state.geopackageBrowserWindowState = action.payload
		},

		// Camera state
		setRapidCameraMovement: (state, action: PayloadAction<boolean>) => {
			state.rapidCameraMovement = action.payload
		},

		// Clock speed cap from the focused entity (null = uncapped)
		setClockSpeedCap: (state, action: PayloadAction<number | null>) => {
			state.clockSpeedCap = action.payload
		},

		setMobileInfoSheetOpen: (state, action: PayloadAction<boolean>) => {
			state.mobileInfoSheetOpen = action.payload
			if (!action.payload) state.mobileInfoSheetHeight = 0
		},
		setMobileInfoSheetHeight: (state, action: PayloadAction<number>) => {
			state.mobileInfoSheetHeight = action.payload
		},

		// Open (quake id) or close (null) the earthquake cutout block
		setQuakeCutout: (state, action: PayloadAction<string | null>) => {
			if (action.payload === null) {
				state.quakeCutout = null
				state.quakeCutoutSheetHeight = 0
			} else if (state.quakeCutout?.quakeId !== action.payload) {
				state.quakeCutout = {
					quakeId: action.payload,
					status: "loading",
					stages: { sequence: false, terrain: false, history: false, detail: false, slab: false, exposure: false },
					hudCollapsed: false
				}
			}
		},
		// Engine-side progress for the open cutout (ignored if it was closed
		// meanwhile): shallow-merges the given fields, stages included
		updateQuakeCutout: (state, action: PayloadAction<QuakeCutoutUpdate>) => {
			const current = state.quakeCutout
			if (current?.quakeId !== action.payload.quakeId) return
			const { stages, ...rest } = action.payload
			Object.assign(current, rest)
			if (stages) {
				current.stages = { ...current.stages, ...stages }
			}
		},
		setQuakeCutoutSheetHeight: (state, action: PayloadAction<number>) => {
			state.quakeCutoutSheetHeight = action.payload
		},

		// Open (storm id) or close (null) the storm column block
		setStormCutout: (state, action: PayloadAction<string | null>) => {
			if (action.payload === null) {
				state.stormCutout = null
			} else if (state.stormCutout?.stormId !== action.payload) {
				state.stormCutout = {
					stormId: action.payload,
					status: "loading",
					stages: { frames: false, wind: false, forecast: false, sst: false, buoys: false, lightning: false },
					hudCollapsed: false
				}
			}
		},
		// Engine-side progress for the open storm column (ignored if it was
		// closed meanwhile): shallow-merges the given fields, stages included
		updateStormCutout: (state, action: PayloadAction<StormCutoutUpdate>) => {
			const current = state.stormCutout
			if (current?.stormId !== action.payload.stormId) return
			const { stages, ...rest } = action.payload
			Object.assign(current, rest)
			if (stages) {
				current.stages = { ...current.stages, ...stages }
			}
		},
		setStormWindProbabilityKt: (state, action: PayloadAction<34 | 50 | 64>) => {
			state.stormWindProbabilityKt = action.payload
		},
		// Hide or show a cutout HUD behind its chip; the block stays open
		setCutoutHudCollapsed: (state, action: PayloadAction<{ cutout: "quake" | "storm"; collapsed: boolean }>) => {
			const target = action.payload.cutout === "quake" ? state.quakeCutout : state.stormCutout
			if (target) target.hudCollapsed = action.payload.collapsed
		},

		// Multi-window management
		openDatasetWindow: (state, action: PayloadAction<Omit<DatasetBrowserWindowState, 'zIndex'>>) => {
			const windowId = action.payload.id
			const zIndex = state.nextZIndex++

			state.datasetWindows[windowId] = {
				...action.payload,
				zIndex,
			}

			// Add to window order (at the front)
			state.windowOrder = state.windowOrder.filter(id => id !== windowId).concat(windowId)
		},

		closeDatasetWindow: (state, action: PayloadAction<string>) => {
			const windowId = action.payload
			delete state.datasetWindows[windowId]
			state.windowOrder = state.windowOrder.filter(id => id !== windowId)
		},

		updateDatasetWindow: (state, action: PayloadAction<{ id: string; updates: Partial<DatasetBrowserWindowState> }>) => {
			const { id, updates } = action.payload
			if (state.datasetWindows[id]) {
				state.datasetWindows[id] = { ...state.datasetWindows[id], ...updates }
			}
		},

		bringDatasetWindowToFront: (state, action: PayloadAction<string>) => {
			const windowId = action.payload
			if (state.datasetWindows[windowId]) {
				// Remove from current position and add to front
				state.windowOrder = state.windowOrder.filter(id => id !== windowId).concat(windowId)
				// Update z-index
				state.datasetWindows[windowId].zIndex = state.nextZIndex++
			}
		},

		toggleMaximizeDatasetWindow: (state, action: PayloadAction<string>) => {
			const windowId = action.payload
			if (state.datasetWindows[windowId]) {
				state.datasetWindows[windowId].isMaximized = !state.datasetWindows[windowId].isMaximized
			}
		},

		toggleDatasetWindowVisibility: (state, action: PayloadAction<string>) => {
			const windowId = action.payload
			if (state.datasetWindows[windowId]) {
				state.datasetWindows[windowId].isVisible = !state.datasetWindows[windowId].isVisible
			}
		},

		setDatasetWindowGeoPackageLayer: (state, action: PayloadAction<{
			windowId: string
			catalogId: string
			activeLayerName: string
		}>) => {
			const { windowId, catalogId, activeLayerName } = action.payload
			if (state.datasetWindows[windowId]) {
				state.datasetWindows[windowId].catalogId = catalogId
				state.datasetWindows[windowId].activeLayerName = activeLayerName
			}
		},

		// Bottom drawer management
		dockWindowToDrawer: (state, action: PayloadAction<string | { windowId: string; openDrawer?: boolean }>) => {
			// Support both string and object payload for backwards compatibility
			const payload = typeof action.payload === 'string'
				? { windowId: action.payload, openDrawer: true }
				: action.payload
			const { windowId, openDrawer = true } = payload

			const window = state.datasetWindows[windowId]
			if (!window) return

			// Check if window can be docked
			const dockMode = window.dockMode ?? 'both'
			if (dockMode === 'window-only') return

			// Mark window as docked
			window.isDockedInDrawer = true
			window.isMaximized = false

			// Check if tab already exists
			const existingTab = state.bottomDrawer.tabs.find(t => t.windowId === windowId)
			if (!existingTab) {
				// Add to drawer tabs
				state.bottomDrawer.tabs.push({
					windowId,
					order: state.bottomDrawer.tabs.length,
				})
			}

			// Set as active tab and optionally open drawer
			state.bottomDrawer.activeTabId = windowId
			if (openDrawer) {
				state.bottomDrawer.isOpen = true
			}

			// Bring frontmost remaining floating window forward
			const visibleFloating = state.windowOrder
				.map(id => state.datasetWindows[id])
				.filter(w => w && !w.isDockedInDrawer)

			if (visibleFloating.length > 0) {
				const topmost = visibleFloating.reduce((prev, current) =>
					current.zIndex > prev.zIndex ? current : prev
				)
				topmost.zIndex = state.nextZIndex++
			}
		},

		undockTabToWindow: (state, action: PayloadAction<{ windowId: string; x?: number; y?: number }>) => {
			const { windowId, x, y } = action.payload
			const window = state.datasetWindows[windowId]
			if (!window) return

			// Check if window can be undocked to a window
			const dockMode = window.dockMode ?? 'both'
			if (dockMode === 'dock-only') return

			// Restore as floating window
			window.isDockedInDrawer = false

			// Set position if provided (from drag), otherwise use stored position
			if (x !== undefined && y !== undefined) {
				window.x = x
				window.y = y
			}

			// Bring to front
			window.zIndex = state.nextZIndex++
			state.windowOrder = state.windowOrder.filter(id => id !== windowId).concat(windowId)

			// Remove from drawer tabs
			state.bottomDrawer.tabs = state.bottomDrawer.tabs.filter(t => t.windowId !== windowId)

			// Update tab orders
			state.bottomDrawer.tabs.forEach((tab, index) => {
				tab.order = index
			})

			// If this was the active tab, select another
			if (state.bottomDrawer.activeTabId === windowId) {
				state.bottomDrawer.activeTabId = state.bottomDrawer.tabs.length > 0
					? state.bottomDrawer.tabs[state.bottomDrawer.tabs.length - 1].windowId
					: null
			}

			// If no tabs left, close drawer
			if (state.bottomDrawer.tabs.length === 0) {
				state.bottomDrawer.isOpen = false
			}
		},

		setActiveDrawerTab: (state, action: PayloadAction<string>) => {
			const windowId = action.payload
			if (state.bottomDrawer.tabs.some(t => t.windowId === windowId)) {
				state.bottomDrawer.activeTabId = windowId
			}
		},

		closeDrawerTab: (state, action: PayloadAction<string>) => {
			const windowId = action.payload

			// Remove from drawer tabs
			state.bottomDrawer.tabs = state.bottomDrawer.tabs.filter(t => t.windowId !== windowId)

			// Update tab orders
			state.bottomDrawer.tabs.forEach((tab, index) => {
				tab.order = index
			})

			// If this was the active tab, select another
			if (state.bottomDrawer.activeTabId === windowId) {
				state.bottomDrawer.activeTabId = state.bottomDrawer.tabs.length > 0
					? state.bottomDrawer.tabs[state.bottomDrawer.tabs.length - 1].windowId
					: null
			}

			// Close the window entirely
			delete state.datasetWindows[windowId]
			state.windowOrder = state.windowOrder.filter(id => id !== windowId)

			// If no tabs left, close drawer
			if (state.bottomDrawer.tabs.length === 0) {
				state.bottomDrawer.isOpen = false
			}
		},

		setDrawerHeight: (state, action: PayloadAction<number>) => {
			state.bottomDrawer.height = Math.max(200, Math.min(action.payload, window.innerHeight - 100))
		},

		toggleDrawer: (state) => {
			state.bottomDrawer.isOpen = !state.bottomDrawer.isOpen
		},

		closeDrawer: (state) => {
			state.bottomDrawer.isOpen = false
		},

		openDrawer: (state) => {
			state.bottomDrawer.isOpen = true
		},

		reorderDrawerTabs: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
			const { fromIndex, toIndex } = action.payload
			const tabs = state.bottomDrawer.tabs

			if (fromIndex < 0 || fromIndex >= tabs.length || toIndex < 0 || toIndex >= tabs.length) {
				return
			}

			const [movedTab] = tabs.splice(fromIndex, 1)
			tabs.splice(toIndex, 0, movedTab)

			// Update order values
			tabs.forEach((tab, index) => {
				tab.order = index
			})
		},

		// Local data scan results (transient state)
		setLocalDataScanResults: (state, action: PayloadAction<{
			files: LocalDataFile[]
			scannedAt: number
			errors: string[]
		}>) => {
			const { files, scannedAt, errors } = action.payload
			state.localDataFiles = files
			state.localDataLastScan = scannedAt
			state.localDataScanErrors = errors
		},
		clearLocalDataScanResults: (state) => {
			state.localDataFiles = []
			state.localDataLastScan = null
			state.localDataScanErrors = []
		},
		// Git integration
		setGitHistoryOpen: (state, action: PayloadAction<boolean>) => {
			state.gitHistoryOpen = action.payload
		},
		setGitCurrentBranch: (state, action: PayloadAction<string | null>) => {
			state.gitCurrentBranch = action.payload
		},
		setGitHasChanges: (state, action: PayloadAction<boolean>) => {
			state.gitHasChanges = action.payload
		},
		// Saved-view save toast (null hides it)
		setSavedViewToast: (state, action: PayloadAction<string | null>) => {
			state.savedViewToastId = action.payload
		},
		// Automated camera motion mode (dispatched by CameraDirector only)
		setCameraMode: (state, action: PayloadAction<DialogState["cameraMode"]>) => {
			state.cameraMode = action.payload
		},
		// Device location tracking (dispatched by userLocationService only)
		updateUserLocation: (state, action: PayloadAction<Partial<UserLocationState>>) => {
			Object.assign(state.userLocation, action.payload)
		},
		clearUserLocation: (state) => {
			state.userLocation = { ...initialState.userLocation, headingSupported: state.userLocation.headingSupported }
		},
		// Story playback position (dispatched by StoryPlayer only)
		setActiveStory: (state, action: PayloadAction<DialogState["activeStory"]>) => {
			state.activeStory = action.payload
		},
		// Open/close the story editor (id of the story being authored)
		setStoryEditor: (state, action: PayloadAction<string | null>) => {
			state.storyEditorId = action.payload
			// Closing the editor abandons any in-progress drawing
			if (action.payload === null) {
				state.storyDrawing = null
			}
		},
		// Begin click-to-draw annotation authoring on the globe
		startStoryDrawing: (state, action: PayloadAction<{ storyId: string; kind: "arc" | "marker" | "region" }>) => {
			state.storyDrawing = { ...action.payload, points: [] }
		},
		// Globe click while drawing (dispatched by the engine's pin handler)
		addStoryDrawingPoint: (state, action: PayloadAction<{ lat: number; lon: number }>) => {
			if (!state.storyDrawing) return
			state.storyDrawing.points.push(action.payload)
		},
		cancelStoryDrawing: (state) => {
			state.storyDrawing = null
		},
		setLibraryTab: (state, action: PayloadAction<LibraryTab>) => {
			state.libraryTab = action.payload
		},
		setSavedViewsSortMode: (state, action: PayloadAction<DialogState["savedViewsSortMode"]>) => {
			state.savedViewsSortMode = action.payload
		},
		setMyPlacesSortMode: (state, action: PayloadAction<DialogState["myPlacesSortMode"]>) => {
			state.myPlacesSortMode = action.payload
		},
		// Device seat-limit eviction token (null closes the dialog)
		setDeviceLimitToken: (state, action: PayloadAction<string | null>) => {
			state.deviceLimitToken = action.payload
		},
	},
})

export const {
	setBetaTestOpen,
	setProfileOpen,
	setAuthOpen,
	setActiveShare,
	setSharedPlacemarkPreview,
	setSharedStoryPreview,
	setSupporterOfferOpen,
	setSubscribeOpen,
	setCheckoutOpen,
	setSelectedSideTable,
	setPackageLoadingOpen,
	setWelcomeOpen,
	setOnboardingOpen,
	setDropPinMenu,
	setWebcamSheet,
	setLayersPanelOpen,
	setLegendSheet,
	setLegendTrayHidden,
	openCommandPalette,
	closeCommandPalette,
	setSelectedDevtoolsTable,
	// Data browser dialogs
	setShapefileBrowserOpen,
	setGeoJSONBrowserOpen,
	// Open dataset files
	addOpenCSVFile,
	removeOpenCSVFile,
	clearOpenCSVFiles,
	// Data browser window states
	setCSVBrowserWindowState,
	setShapefileBrowserWindowState,
	setGeoJSONBrowserWindowState,
	setGeoPackageBrowserWindowState,
	// Camera state
	setRapidCameraMovement,
	setClockSpeedCap,
	setMobileInfoSheetOpen,
	setMobileInfoSheetHeight,
	setQuakeCutout,
	updateQuakeCutout,
	setStormCutout,
	updateStormCutout,
	setCutoutHudCollapsed,
	setStormWindProbabilityKt,
	setQuakeCutoutSheetHeight,
	// Multi-window management
	openDatasetWindow,
	closeDatasetWindow,
	updateDatasetWindow,
	bringDatasetWindowToFront,
	toggleMaximizeDatasetWindow,
	toggleDatasetWindowVisibility,
	setDatasetWindowGeoPackageLayer,
	// Bottom drawer management
	dockWindowToDrawer,
	undockTabToWindow,
	setActiveDrawerTab,
	closeDrawerTab,
	setDrawerHeight,
	toggleDrawer,
	closeDrawer,
	openDrawer,
	reorderDrawerTabs,
	// Local data scan results
	setLocalDataScanResults,
	clearLocalDataScanResults,
	// Git integration
	setGitHistoryOpen,
	setGitCurrentBranch,
	setGitHasChanges,
	setSavedViewToast,
	setCameraMode,
	updateUserLocation,
	clearUserLocation,
	setActiveStory,
	setStoryEditor,
	startStoryDrawing,
	addStoryDrawingPoint,
	cancelStoryDrawing,
	setLibraryTab,
	setSavedViewsSortMode,
	setMyPlacesSortMode,
	setDeviceLimitToken,
} = dialogSlice.actions

export default dialogSlice.reducer
